/**
 * Hello fellow programmer
 * This is the file with all QUnit Field types.
 *
 * All QUnit fields MUST inherit from the QUnitField super class.
 * An example of the structure for an specific QUnit field can be found at
 * `class QUnitFieldDateTime`. Just do CTRL+F ;)
 *
 * QUnitField instances are inmutable (ideally), if you want to change a value,
 * you must do so with:
 * field = new QUnitFieldMyType({...field.props, value: new_value})
 *
 * ADDING A NEW QFIELD:
 *   1. CTRL+C any QUnitField* class that you like and name it QUnitFieldXXX.
 *   2. Code the methods accordingly to your needs.
 *   3. Don't forget to add the class to export default at the end of this file.
 *   4. Don't forget to add a mapping of backend's API type in fields_mapping.js
 *
 * NOTES:
 *   As of 2023-04-24. serialize() is not used,
 *     is just here in case we needed in the future
 **/

class QUnitField {
  /**
   * Super class for all QUnit fields
   **/
  constructor(props) {
    if (props instanceof QUnitField) {
      props = props.props;
    }

    this.props = props;

    if (props.value === undefined)
      this._value = undefined;
    else 
      this._value = this.parse(props.value);

    this.cache = true;
    this._cache_string = undefined;
  }

  parse(raw) {
    // raw is an string
    // Returns the parsed value
    return raw;
  }

  render(value) {
    // value is a value parsed with parse(raw)
    // Returns an string representation
    // return ''+value;
    console.warn("No render to this field", this.props);
    return `${value}`;
  }

  serialize(value) {
    // value is a value parsed with parse(raw)
    // Returns an string that can be parsed by parse()
    return this.render(value);
  }

  component() {
    // Returns the component name to represent the field
    return 'FieldString';
  }

  is_empty() {
    return this._value === undefined || this._value === null;
  }

  clone(overwrite) {
    // Clone the field
    // overwrite is a dictionary to overwritte properties
    overwrite = overwrite || {};
    return new this.constructor({ ...this.props, ...overwrite });
  }

  toString() {
    if (this._value === undefined)
      return '';
    if (this.cache === false)
      return this.render(this._value);
    else if (this._cache_string === undefined)
      this._cache_string = this.render(this._value);
    return this._cache_string;
  }

  valueOf() {
    return this.toString();
  }

  toJSON() {
    return this.props; //this.serialize(this._value === undefined ? null : this._value)
  }

  comparable() {
    // Return a comparable value for sorting fields of the same type
    return this._value;
  }

  get value() {
    return this._value;
  }

  get string() {
    return this.toString();
  }

  get name() {
    return this.constructor.name;
  }

  get id() {
    return this.props.id;
  }
}

class QUnitFieldFallback extends QUnitField {
  constructor(props) {
    super(props);
    this.cache = false;
  }

  parse(raw) {
    return this.props.readable === undefined ? raw : this.props.readable;
  }
}

class QUnitFieldString extends QUnitField {
  /**
   * String field
   **/
  constructor(props) {
    super(props);
    this.cache = false;
  }

  render(value) {
    return value ? '' + value : '';
  }

  comparable() {
    return '' + this;
  }
}

class QUnitFieldStringArea extends QUnitFieldString {
  /**
   * Multiline string field
   **/
  component() {
    return 'FieldStringArea';
  }
}

class QUnitFieldNumeric extends QUnitField {
  /**
   * Numeric (integer) field
   **/
  parse(raw) {
    return raw ? raw * 1.0 : undefined;
  }

  render(value) {
    // return value === undefined ? '' : '' + value;
    return value === undefined 
      ? ''
      : (+value).toLocaleString(
        undefined, // TODO: UserLang
        {minimumFractionDigits: this.props.decimals || 0}
      );
  }
}

class QUnitFieldDecimal extends QUnitField {
  /**
   * Numeric (float) field
   **/
  parse(raw) {
    return raw ? raw * 1.0 : undefined;
  }

  render(value) {
    // return value === undefined ? '' : '' + value;
    console.log(this.props);
    const z = value === undefined 
          ? ''
          : (+value).toLocaleString(
            undefined, // TODO: UserLang
            {maximumFractionDigits: this.props.decimals || 2}
          );
    return z;
  }
}

class QUnitFieldDateTime extends QUnitField {
  /**
   * DateTime field. Parses YYYY-MM-DDTHH:mm:ss in UTC
   **/
  parse(raw) {
    return raw ? new Date(raw) : undefined;
  }

  render(value) {
    if (! this._value)
      return '';
    return value.toLocaleDateString(undefined, {
      // weekday: 'long',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  // Custom method
  short(value) {
    if (!this._value)
      return '';
    return this.render(value); // TODO
  }

  serialize(value) {
    return value.toISOString();
  }
}

class QUnitFieldDate extends QUnitField {
  /**
   * Date field. Parses YYYY-MM-DD
   **/
  parse(raw) {
    return raw ? new Date(raw) : undefined;
  }

  render(value) {
    if (!this._value)
      return '';
    return value.toLocaleDateString(undefined, {
      // weekday: 'long',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  }

  // Custom method
  short() {
    if (!this._value)
      return '';
    return this._value.toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  serialize(value) {
    return value.toISOString();
  }

  limit_time() {
    return this.props.limit_date ? this.props.limit_date : '';
  }

  component() {
    return 'FieldDate';
  }
}

class QUnitFieldLink extends QUnitFieldString {
  /**
   * Link field
   **/

  component() {
    return 'FieldLink';
  }
}



class QUnitFieldFile extends QUnitField {
  /**
   * File field
   **/

  component() {
    return 'FieldFile';
  }

  render(value) {
    return this.props.readable === undefined ? '' : this.props.readable;
  }

  mimetype() {
    return this.props.mimetype;
  }

  get version() {
    return this.props.version;
  }

  get downloadable_title() {
    return this.props.downloadable_title
      ? this.props.downloadable_title
      : this.props.title;
  }

  comparable() {
    return ''+this;
  }

  get attribute() {
    return this.props.attribute;
  }

  get title() {
    return this.props.readable;
  }

  get qunit_id() {
    return this.props.qunit_id;
  }

  get pmethod() {
    return this.props.pmethod;
  }

}

class QUnitFieldImage extends QUnitFieldFile {
  /**
   * Image field
   **/

  component() {
    return 'FieldImage';
  }
}

class QUnitFieldRelation extends QUnitField {
  /**
   * Generic Relation field.
   **/

  render(value) {
    return (this.props.readable === undefined || this.props.readable === null ) ? '' : this.props.readable;
  }

  component() {
    return 'FieldString';
  }

  comparable() {
    return ''+this;
  }
}

class QUnitFieldRelationLink extends QUnitFieldRelation {
  component() {
    return 'FieldRelated'
  }

  render() {
    return this.props.readable === undefined ? '' 
    : (this.props.readable instanceof Array ? this.props.readable.join(', ') : this.props.readable);
  }
}

class QUnitFieldState extends QUnitFieldString {
  /**
   * String state field
   **/
  component() {
    return 'FieldState';
  }

  dot_color() {
    return this.props.color ? this.props.color : '#ffffff';
  }

  readable_text() {
    return this.props.readable;
  }

  comparable() {
    return ''+this;
  }
}

class QUnitFieldTimeEvent extends QUnitField {
  // TODO component

  render(value) {
    return value && this.props.readable ? this.props.readable : value || "";
  }
}


class QUnitVersionableFieldFile extends QUnitFieldFile {
  /**
   * File field
   **/

  component() {
    return 'FieldVersionableFile';
  }

  render(raw) {
    return (this.props.readable === undefined ? raw : this.props.readable);
  }

  get version() {
    return this.props.version;
  }

  get downloadable_title() {
    return this.props.downloadable_title
      ? this.props.downloadable_title
      : this.props.title;
  }

  comparable() {
    return ''+this;
  }

  get attribute() {
    return this.props.attribute;
  }

  get title() {
    return this.props.readable;
  }

  get is_convertible() {
    return this.props.convertible_to_pdf;    
  }

  get qunit_id() {
    return this.props.qunit_id;
  }
  
  get download_options() {
    return this.props.download_options;
  }

  get pmethod() {
    return this.props.pmethod;
  }
}

class QUnitFieldUser extends QUnitFieldString {
  constructor(props) {
    super(props);
    this.cache = false;
  }

  component() {
    return 'FieldUser'
  }

  comparable() {
    return this.value; 
  }

  render(raw) {
    return ''+(this.props.readable === undefined ? raw : this.props.readable);
  }
}

class QUnitFieldIndicatorTracking extends QUnitFieldString {
  /**
   * Indicator tracking values
   **/
  component() {
    return 'FieldIndicatorTracking';
  }

  render(value) {
    return this.props.readable;
  }

  comparable() {
    return Object.values(this.value).join('|');
  }
}

class QUnitFieldCentro extends QUnitField {
  component() {
    return 'FieldCentro';
  }

  render(value) {
    return this.value;
  }

  get logo() {
    return this.props.logo;
  }

  get text() {
    return this.props.readable;
  }
}


class QUnitFieldIndicatorRiskInterpreter extends QUnitField {
  /**
   * Risk interpreter range list
   **/
  constructor(props) {
    super(props);
    this.cache = false;
  }

  component() {
    return 'FieldIndicatorRiskInterpretation';
  }

  render(value) {
    return this.label;
  }

  comparable() {
    return this.render();
  }

  get color() {
    return this.value.color;
  }

  get label() {
    return this.value.label;
  }
}


class QUnitFieldIndicatorRiskInterpretation extends QUnitField {
  /**
   * Risk interpretation value
   **/
  constructor(props) {
    super(props);
    this.cache = false;
  }

  component() {
    return 'FieldIndicatorRiskInterpretation';
  }

  render(value) {
    return this.label;
  }

  comparable() {
    return this.render();
  }

  get color() {
    return this.value === null ? '#ffffff00' : this.value.color;
  }

  get label() {
    return this.value === null ? '' : this.value.label;
  }
}


export default {
  QUnitFieldFallback: QUnitFieldFallback,
  QUnitField: QUnitField,
  QUnitFieldString: QUnitFieldString,
  QUnitFieldNumeric: QUnitFieldNumeric,
  QUnitFieldDecimal: QUnitFieldDecimal,
  QUnitFieldDate: QUnitFieldDate,
  QUnitFieldDateTime: QUnitFieldDateTime,
  QUnitFieldImage: QUnitFieldImage,
  QUnitFieldLink: QUnitFieldLink,
  QUnitFieldFile: QUnitFieldFile,
  QUnitFieldStringArea: QUnitFieldStringArea,
  QUnitFieldRelation: QUnitFieldRelation,
  QUnitFieldRelationLink: QUnitFieldRelationLink,
  QUnitFieldTimeEvent: QUnitFieldTimeEvent,
  QUnitFieldState: QUnitFieldState,
  QUnitVersionableFieldFile: QUnitVersionableFieldFile,
  QUnitFieldUser: QUnitFieldUser,
  QUnitFieldIndicatorTracking: QUnitFieldIndicatorTracking,
  QUnitFieldCentro: QUnitFieldCentro,
  QUnitFieldIndicatorRiskInterpretation: QUnitFieldIndicatorRiskInterpretation,
};
